import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box, Button, Chip, CircularProgress, FormControl, InputAdornment, InputLabel, MenuItem, Pagination, Paper, Select,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, ToggleButton,
  ToggleButtonGroup, Typography, alpha,
} from '@mui/material';
import { Search as SearchIcon, LocalOffer as LocalOfferIcon } from '@mui/icons-material';
import { useNotify } from 'react-admin';
import authInstance from '../../services/httpClient';
import { useAdminTheme } from '../../admin-theme';
import NameRulesEditor, { errorMessage } from './NameRulesEditor';
import OptionTagSwitches, { OptionRole, RefreshResult, refreshNotice } from './OptionTagSwitches';
import { stagingApiBase, stagingOrg } from './stagingOrg';
import AddTagDialog, { NewTag } from './AddTagDialog';
import { useTagChangeConfirm } from './TagChangeConfirm';
import { useScrollToListTop } from '../../hooks/useScrollToListTop';

// Tags tab (spec section 6): one row per Vend tag seen on staged items or given a
// role. Every tag is Chip unless set to Ignore or Exclude (no role saved = Chip): Chip keeps it
// on the product; Ignore = dropped in the adapter so it never reaches product.tags; Exclude
// (29 Oct 2026) = items carrying it are withdrawn and live products carrying it hidden. "Also show on category" is the optional, older
// tag-mapping behaviour (vaperite_tag_category_mappings).

export type TagRole = 'chip' | 'ignore' | 'exclude';

export type TagSource = 'vend_tag' | 'type' | 'option' | 'name_rule' | 'manual';

const SOURCE_LABEL: Record<TagSource, string> = {
  vend_tag: 'Vend tag', type: 'From type', option: 'Variant option', name_rule: 'Name rule', manual: 'Manual',
};

/** A tag only people typed in: no sync rebuilds it, so Display As has nothing to rename. */
const manualOnly = (t: TagRow) => (t.sources ?? []).length > 0 && (t.sources ?? []).every((s) => s === 'manual');

export interface TagRow {
  tag: string;
  role: TagRole | null;
  /** What the chip says in the app; null = the tag's own name (Jason, 28 Oct 2026). */
  displayAs?: string | null;
  sources: TagSource[];
  productCount: number;
  alsoShowOnCategoryId: string | null;
}

interface TagMapping { id: number; tagName: string; localCategoryId: string | null }
interface Category { id: string; name: string }
type RoleFilter = 'all' | TagRole;

// No saved role behaves as Chip: the sync only acts on Ignore.
const roleOf = (t: TagRow): TagRole => t.role ?? 'chip';

const key = (s: string) => s.trim().toLowerCase();

/**
 * Display As: typed in, saved on Enter or leaving the field. Only a Chip shows in the app, so an
 * Ignore tag's field is greyed out (its value is kept for when it is a Chip again).
 */
function DisplayAsField({ row, disabled, onSave }: { row: TagRow; disabled: boolean; onSave: (value: string) => Promise<boolean> }) {
  const saved = row.displayAs ?? '';
  const [value, setValue] = useState(saved);
  useEffect(() => { setValue(saved); }, [saved]);
  // a refused name (e.g. a comma) goes back to the saved one rather than being re-sent on every blur
  const commit = async () => {
    if (value.trim() === saved) return;
    if (!(await onSave(value.trim()))) setValue(saved);
  };
  return (
    <TextField
      size="small"
      value={value}
      placeholder={row.tag}
      disabled={disabled}
      onChange={(e) => setValue(e.target.value)}
      onBlur={commit}
      onKeyDown={(e) => { if (e.key === 'Enter') (e.target as HTMLInputElement).blur(); if (e.key === 'Escape') setValue(saved); }}
      inputProps={{ 'aria-label': `Display ${row.tag} as`, maxLength: 60 }}
      sx={{ minWidth: 160 }}
    />
  );
}

/** Tag rows per page (192+ in one table was too long to work through). */
const TAGS_PER_PAGE = 50;

export default function TagsTab() {
  const isDeeliver = stagingOrg() === 'deeliver';
  const { colors, mode } = useAdminTheme();
  // The selected role's colour (Jason, 29 Oct 2026): Chip green, Ignore orange, Exclude red.
  const roleColour: Record<TagRole, string> = {
    chip: colors.success,
    ignore: mode === 'dark' ? '#FF9A3C' : '#B85A00',
    exclude: colors.error,
  };
  const roleButton = (role: TagRole) => ({
    textTransform: 'none' as const,
    py: 0.25,
    '&.Mui-selected, &.Mui-selected:hover': {
      color: roleColour[role],
      bgcolor: alpha(roleColour[role], 0.16),
      borderColor: alpha(roleColour[role], 0.5),
      fontWeight: 700,
    },
  });
  const notify = useNotify();
  const [tags, setTags] = useState<TagRow[]>([]);
  const [mappings, setMappings] = useState<TagMapping[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState<RoleFilter>('all');
  const [savingTag, setSavingTag] = useState<string | null>(null);
  const [options, setOptions] = useState<OptionRole[]>([]);
  const [optionsLoading, setOptionsLoading] = useState(true);
  const [savingOption, setSavingOption] = useState<string | null>(null);
  const [reapplying, setReapplying] = useState(false);
  const [page, setPage] = useState(1);
  const listTopRef = useScrollToListTop(page);
  const [adding, setAdding] = useState(false);
  const { confirm, dialog: confirmDialog } = useTagChangeConfirm();
  const [savingNew, setSavingNew] = useState(false);

  const loadTags = useCallback(async () => {
    try {
      const { data } = await authInstance.get(`${stagingApiBase()}/staging/tags`);
      setTags(data.tags || []);
    } catch (err) {
      notify(errorMessage(err, 'Could not load tags'), { type: 'error' });
    } finally {
      setLoading(false);
    }
  }, [notify]);

  const loadOptions = useCallback(async () => {
    if (isDeeliver) { setOptionsLoading(false); return; }
    try {
      const { data } = await authInstance.get(`${stagingApiBase()}/staging/option-roles`);
      setOptions(data.options || []);
    } catch (err) {
      notify(errorMessage(err, 'Could not load variant options'), { type: 'error' });
    } finally {
      setOptionsLoading(false);
    }
  }, [notify, isDeeliver]);

  // After any mutation: snackbar with the refreshed count, then refetch what the change touched.
  const afterMutation = useCallback((res: RefreshResult | undefined, saved?: string) => {
    const n = refreshNotice(res, saved);
    notify(n.message, { type: n.type });
    loadTags();
    loadOptions();
  }, [notify, loadTags, loadOptions]);

  const loadMappings = useCallback(async () => {
    try {
      const { data } = await authInstance.get(`${stagingApiBase()}/staging/tag-mappings`);
      setMappings(data.mappings || []);
    } catch (err) {
      notify(errorMessage(err, 'Could not load tag categories'), { type: 'error' });
    }
  }, [notify]);

  useEffect(() => {
    loadTags();
    loadOptions();
    authInstance.get('categories?limit=100')
      .then(({ data }: any) => setCategories(data.data || data.categories || (Array.isArray(data) ? data : [])))
      .catch(() => {});
    loadMappings();
  }, [loadTags, loadOptions, loadMappings]);

  const mappingByTag = useMemo(() => {
    const m = new Map<string, TagMapping>();
    for (const x of mappings) if (!m.has(key(x.tagName))) m.set(key(x.tagName), x);
    return m;
  }, [mappings]);

  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    return tags.filter((t) => (!q || t.tag.toLowerCase().includes(q) || (t.displayAs ?? '').toLowerCase().includes(q))
      && (roleFilter === 'all' || roleOf(t) === roleFilter));
  }, [tags, search, roleFilter]);

  // Back to page 1 whenever the filter changes what is listed.
  useEffect(() => { setPage(1); }, [search, roleFilter]);
  const pageCount = Math.max(1, Math.ceil(visible.length / TAGS_PER_PAGE));
  const pageRows = visible.slice((Math.min(page, pageCount) - 1) * TAGS_PER_PAGE, Math.min(page, pageCount) * TAGS_PER_PAGE);

  const setRole = async (tag: string, role: TagRole | null) => {
    if (!role) return; // a role can be switched, not cleared
    setSavingTag(tag);
    try {
      if (!(await confirm({ kind: 'role', tag, role }))) return;
      const { data } = await authInstance.put(`${stagingApiBase()}/staging/tags/${encodeURIComponent(tag)}/role`, { role });
      setTags((ts) => ts.map((t) => (t.tag === tag ? { ...t, role } : t)));
      const plural = (n: number, one: string) => `${n} ${one}${n === 1 ? '' : 's'}`;
      let saved = 'Role saved';
      if (role === 'exclude') saved = `Excluded: ${plural(data?.hidden ?? 0, 'live product')} hidden, ${plural(data?.tagWithdrawn ?? 0, 'item')} withdrawn`;
      else if (data?.unhidden || data?.tagRestored) saved = `Role saved: ${plural(data?.unhidden ?? 0, 'live product')} shown again, ${plural(data?.tagRestored ?? 0, 'item')} back in review`;
      afterMutation(data, saved);
    } catch (err) {
      notify(errorMessage(err, 'Could not set the role'), { type: 'error' });
    } finally {
      setSavingTag(null);
    }
  };

  const setDisplayAs = async (tag: string, displayAs: string): Promise<boolean> => {
    setSavingTag(tag);
    try {
      if (!(await confirm({ kind: 'display', tag, displayAs: displayAs || null }))) return false;
      const { data } = await authInstance.put(`${stagingApiBase()}/staging/tags/${encodeURIComponent(tag)}/display-as`, { displayAs: displayAs || null });
      setTags((ts) => ts.map((t) => (t.tag === tag ? { ...t, displayAs: data?.tag?.displayAs ?? (displayAs || null), role: t.role ?? 'chip' } : t)));
      const live = data?.liveRetagged ? ` (${data.liveRetagged} live product${data.liveRetagged === 1 ? '' : 's'} renamed)` : '';
      afterMutation(data, `${displayAs ? 'Display name saved' : 'Display name cleared'}${live}`);
      return true;
    } catch (err) {
      notify(errorMessage(err, 'Could not save the display name'), { type: 'error' });
      return false;
    } finally {
      setSavingTag(null);
    }
  };

  const addTag = async (input: NewTag) => {
    setSavingNew(true);
    try {
      const { data } = await authInstance.post(`${stagingApiBase()}/staging/tags`, input);
      if (data?.tag?.mappingError) notify(`Added ${input.tag}, but the category was not set: ${data.tag.mappingError}`, { type: 'warning' });
      else notify(`Added ${input.tag}`, { type: 'success' });
      setAdding(false);
      setSearch(input.tag);
      loadTags();
      loadMappings();
    } catch (err) {
      notify(errorMessage(err, 'Could not add the tag'), { type: 'error' });
    } finally {
      setSavingNew(false);
    }
  };

  const toggleOption = async (optionName: string, enabled: boolean) => {
    setSavingOption(optionName);
    try {
      if (!(await confirm({ kind: 'option', name: optionName, enabled }))) return;
      const { data } = await authInstance.put(`${stagingApiBase()}/staging/option-roles/${encodeURIComponent(optionName)}`, { enabled });
      afterMutation(data, enabled ? `${optionName} tags on` : `${optionName} tags off`);
    } catch (err) {
      notify(errorMessage(err, 'Could not switch the option'), { type: 'error' });
    } finally {
      setSavingOption(null);
    }
  };

  const reapply = async () => {
    setReapplying(true);
    try {
      if (!(await confirm({ kind: 'reapply' }))) return;
      const { data } = await authInstance.post(`${stagingApiBase()}/staging/tags/refresh-pending`);
      afterMutation(data, 'Re-applied');
    } catch (err) {
      notify(errorMessage(err, 'Could not re-apply tags'), { type: 'error' });
    } finally {
      setReapplying(false);
    }
  };

  const setCategory = async (tag: string, localCategoryId: string) => {
    const existing = mappingByTag.get(key(tag));
    try {
      if (!localCategoryId) {
        if (existing) await authInstance.delete(`${stagingApiBase()}/staging/tag-mappings/${existing.id}`);
      } else if (existing) {
        await authInstance.put(`${stagingApiBase()}/staging/tag-mappings/${existing.id}`, { localCategoryId });
      } else {
        await authInstance.post(`${stagingApiBase()}/staging/tag-mappings`, { tagName: tag, localCategoryId });
      }
      await loadMappings();
    } catch (err) {
      notify(errorMessage(err, 'Could not set the category'), { type: 'error' });
    }
  };

  const head = { color: colors.textMuted, fontWeight: 700, fontSize: '0.7rem', textTransform: 'uppercase' as const, letterSpacing: '0.05em', borderBottom: `1px solid ${colors.border}` };
  const cell = { borderBottom: `1px solid ${colors.border}` };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
      {/* Variant option switches and name rules build Vaperite's variant tags; Deeliver's Vend tags
          arrive as they are, so Deeliver gets the tag list (ignore + tile mapping) only. */}
      {!isDeeliver && <OptionTagSwitches options={options} loading={optionsLoading} savingName={savingOption} onToggle={toggleOption} />}

      <Paper sx={{ p: 2, bgcolor: colors.bgCard, border: `1px solid ${colors.border}`, borderRadius: 2, display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 700, color: colors.textPrimary, width: '100%', mb: -1 }}>Tags</Typography>
        <TextField size="small" placeholder="Search tags..." value={search} onChange={(e) => setSearch(e.target.value)}
          sx={{ minWidth: 200 }}
          inputProps={{ 'aria-label': 'Search tags' }}
          InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon sx={{ fontSize: 18, color: colors.textMuted }} /></InputAdornment> }} />
        <FormControl size="small" sx={{ minWidth: 150 }}>
          <InputLabel id="tag-role-filter">Role</InputLabel>
          <Select labelId="tag-role-filter" label="Role" value={roleFilter} onChange={(e) => setRoleFilter(e.target.value as RoleFilter)}>
            <MenuItem value="all">All</MenuItem>
            <MenuItem value="chip">Chip</MenuItem>
            <MenuItem value="ignore">Ignore</MenuItem>
            <MenuItem value="exclude">Exclude</MenuItem>
          </Select>
        </FormControl>
        <Button variant="outlined" size="small" onClick={reapply} disabled={reapplying} sx={{ textTransform: 'none' }}>
          Re-apply to pending
        </Button>
        <Typography variant="caption" sx={{ color: colors.textMuted }}>
          Every tag is a Chip unless set to Ignore or Exclude. Chip keeps the tag on the product; Ignore drops it at sync so it never reaches the product;
          Exclude withdraws every item carrying it and hides its live products from the app (Chip or Ignore brings them back).
          Display as renames a Chip in the app (empty = its own name); two tags with the same display name become one chip.
        </Typography>
        <Button variant="contained" size="small" onClick={() => setAdding(true)} sx={{ textTransform: 'none', ml: 'auto' }}>
          Add tag
        </Button>
      </Paper>

      <Paper ref={listTopRef} sx={{ bgcolor: colors.bgCard, border: `1px solid ${colors.border}`, borderRadius: 2, overflow: 'hidden' }}>
        {loading ? (
          <Box sx={{ p: 4, display: 'flex', justifyContent: 'center' }}><CircularProgress size={32} /></Box>
        ) : visible.length === 0 ? (
          <Box sx={{ p: 6, textAlign: 'center' }}>
            <LocalOfferIcon sx={{ fontSize: 48, color: colors.textMuted, mb: 1 }} />
            <Typography variant="body2" sx={{ color: colors.textMuted }}>No tags found.</Typography>
          </Box>
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={head}>Tag</TableCell>
                  <TableCell sx={head}>Source</TableCell>
                  <TableCell sx={head}>Products</TableCell>
                  <TableCell sx={head}>Role</TableCell>
                  <TableCell sx={head}>Display as</TableCell>
                  <TableCell sx={head}>Also show on category</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {pageRows.map((t) => (
                  <TableRow key={t.tag}>
                    <TableCell sx={{ ...cell, fontWeight: 600, color: colors.textPrimary }}>{t.tag}</TableCell>
                    <TableCell sx={cell}>
                      <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                        {(t.sources || []).map((s) => <Chip key={s} size="small" label={SOURCE_LABEL[s] ?? s} sx={{ height: 20, fontSize: '0.65rem' }} />)}
                      </Box>
                    </TableCell>
                    <TableCell sx={cell}>{t.productCount}</TableCell>
                    <TableCell sx={cell}>
                      <ToggleButtonGroup size="small" exclusive value={roleOf(t)} disabled={savingTag === t.tag}
                        onChange={(_, v: TagRole | null) => setRole(t.tag, v)} aria-label={`Role for ${t.tag}`}>
                        <ToggleButton value="chip" sx={roleButton('chip')}>Chip</ToggleButton>
                        <ToggleButton value="ignore" sx={roleButton('ignore')}>Ignore</ToggleButton>
                        <ToggleButton value="exclude" sx={roleButton('exclude')}>Exclude</ToggleButton>
                      </ToggleButtonGroup>
                    </TableCell>
                    <TableCell sx={cell}>
                      <DisplayAsField row={t} disabled={roleOf(t) !== 'chip' || manualOnly(t) || savingTag === t.tag} onSave={(v) => setDisplayAs(t.tag, v)} />
                    </TableCell>
                    <TableCell sx={cell}>
                      <FormControl size="small" sx={{ minWidth: 180 }} disabled={roleOf(t) !== 'chip'}>
                        <Select displayEmpty value={mappingByTag.get(key(t.tag))?.localCategoryId ?? ''}
                          onChange={(e) => setCategory(t.tag, e.target.value as string)}
                          inputProps={{ 'aria-label': `Also show ${t.tag} on category` }}>
                          <MenuItem value="">None</MenuItem>
                          {categories.map((c) => <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>)}
                        </Select>
                      </FormControl>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>
      {!loading && (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1, mt: -1.5 }}>
          <Typography variant="caption" sx={{ color: colors.textMuted }}>
            {visible.length} of {tags.length} tag{tags.length !== 1 ? 's' : ''}
            {pageCount > 1 ? ` · showing ${(Math.min(page, pageCount) - 1) * TAGS_PER_PAGE + 1}-${Math.min(Math.min(page, pageCount) * TAGS_PER_PAGE, visible.length)}` : ''}
          </Typography>
          {pageCount > 1 && (
            <Pagination count={pageCount} page={Math.min(page, pageCount)} onChange={(_, p) => setPage(p)} size="small" />
          )}
        </Box>
      )}

      {confirmDialog}
      <AddTagDialog open={adding} categories={categories} saving={savingNew} onClose={() => setAdding(false)} onSave={addTag} />

      {!isDeeliver && <NameRulesEditor confirm={confirm} onMutated={(res, what) => afterMutation(res, `Name rule ${what}`)} />}

      {/* Which Lightspeed tag decides Sell in app, per org (vend-adapter.js). Read-only: it is
          fixed in the sync, so this card only says what the rule reads. */}
      <Paper sx={{ p: 2, bgcolor: colors.bgCard, border: `1px solid ${colors.border}`, borderRadius: 2 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 700, color: colors.textPrimary }}>Sell in app</Typography>
        <Typography variant="caption" sx={{ color: colors.textMuted, display: 'block', mb: 1.5 }}>
          The Lightspeed tag the sync reads to set Sell in app. A person can still override it per product in Products.
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
          <Typography variant="body2" sx={{ color: colors.textSecondary }}>Rule reads tag</Typography>
          <Chip size="small" color="primary" label={isDeeliver ? 'hide-in-app' : 'woocommerce'} sx={{ fontFamily: 'monospace' }} />
          <Typography variant="body2" sx={{ color: colors.textSecondary }}>
            {isDeeliver
              ? 'Tagged = hidden from the app (Sell in app OFF). The tag itself never reaches the product.'
              : 'Tagged = sold in the app (Sell in app ON). Items without it are held for a person to decide.'}
          </Typography>
        </Box>
      </Paper>

      {/* Apply now (Jason, 28 Oct 2026): the same re-apply as the button by the search, at the foot of the page */}
      <Paper sx={{ p: 2, bgcolor: colors.bgCard, border: `1px solid ${colors.border}`, borderRadius: 2, display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
        <Typography variant="body2" sx={{ color: colors.textSecondary, flex: 1, minWidth: 240 }}>
          Every change on this page applies straight away, to live products and to items waiting in Pending Review and Flagged
          Items, after showing how many it will update. Apply now re-runs roles, display names, option switches and name rules on the waiting items.
        </Typography>
        <Button variant="contained" size="small" onClick={reapply} disabled={reapplying} sx={{ textTransform: 'none' }}>
          {reapplying ? 'Applying…' : 'Apply now'}
        </Button>
      </Paper>
    </Box>
  );
}
